import { useAuthStore } from '@/stores/authStore';

/**
 * Customer portal role constants
 * - CUSTOMER_ADMIN: Full access including user management and KVA approval
 * - CUSTOMER_USER: Standard access with optional KVA approval (configurable)
 * - CUSTOMER_READONLY: View-only access, no actions allowed
 */
export const CUSTOMER_ROLES = {
  CUSTOMER_ADMIN: 'CUSTOMER_ADMIN',
  CUSTOMER_USER: 'CUSTOMER_USER',
  CUSTOMER_READONLY: 'CUSTOMER_READONLY',
} as const;

export type CustomerRole = (typeof CUSTOMER_ROLES)[keyof typeof CUSTOMER_ROLES];

export function usePermissions() {
  const user = useAuthStore((state) => state.user);

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) ?? false;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((r) => hasRole(r));
  };

  /**
   * KVA approval requires CUSTOMER_ADMIN role
   * CUSTOMER_USER may have approval rights in the future (configurable per user)
   */
  const canApproveKVA = hasRole(CUSTOMER_ROLES.CUSTOMER_ADMIN);

  /**
   * Check if user is read-only (CUSTOMER_READONLY without higher privileges)
   */
  const isReadOnly =
    hasRole(CUSTOMER_ROLES.CUSTOMER_READONLY) &&
    !hasAnyRole([CUSTOMER_ROLES.CUSTOMER_ADMIN, CUSTOMER_ROLES.CUSTOMER_USER]);

  /**
   * Check if user can create orders (CUSTOMER_ADMIN or CUSTOMER_USER)
   */
  const canCreateOrders = hasAnyRole([CUSTOMER_ROLES.CUSTOMER_ADMIN, CUSTOMER_ROLES.CUSTOMER_USER]);

  /**
   * Check if user can manage portal users (CUSTOMER_ADMIN only)
   */
  const canManageUsers = hasRole(CUSTOMER_ROLES.CUSTOMER_ADMIN);

  return {
    user,
    hasRole,
    hasAnyRole,
    canApproveKVA,
    isReadOnly,
    canCreateOrders,
    canManageUsers,
  };
}
